import type { ICreateGateway, IUpdateGateway } from '@escloud/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context';

export const gatewayKeys = {
    all: ['gateways'] as const,
    list: (tenantId: string) => [...gatewayKeys.all, 'list', tenantId] as const,
    credentials: (tenantId: string, gatewayId: string) =>
        [...gatewayKeys.all, tenantId, gatewayId, 'credentials'] as const,
};

export function useGateways(tenantId: string | undefined) {
    const { gateways } = useApi();
    return useQuery({
        queryKey: gatewayKeys.list(tenantId ?? ''),
        queryFn: () => gateways.listGateways(tenantId!),
        enabled: !!tenantId,
    });
}

export function useCreateGateway(tenantId: string) {
    const { gateways } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: ICreateGateway) => gateways.createGateway(tenantId, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: gatewayKeys.list(tenantId) }),
    });
}

export function useUpdateGateway(tenantId: string) {
    const { gateways } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ gatewayId, data }: { gatewayId: string; data: IUpdateGateway }) =>
            gateways.updateGateway(tenantId, gatewayId, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: gatewayKeys.list(tenantId) }),
    });
}

export function useDeleteGateway(tenantId: string) {
    const { gateways } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (gatewayId: string) => gateways.deleteGateway(tenantId, gatewayId),
        onSuccess: () => qc.invalidateQueries({ queryKey: gatewayKeys.list(tenantId) }),
    });
}

export function useGatewayCredentials(tenantId: string | undefined, gatewayId: string | undefined) {
    const { gateways } = useApi();
    return useQuery({
        queryKey: gatewayKeys.credentials(tenantId ?? '', gatewayId ?? ''),
        queryFn: () => gateways.listCredentials(tenantId!, gatewayId!),
        enabled: !!tenantId && !!gatewayId,
    });
}

export function useIssueCredential(tenantId: string, gatewayId: string) {
    const { gateways } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => gateways.issueCredential(tenantId, gatewayId),
        onSuccess: () => qc.invalidateQueries({ queryKey: gatewayKeys.credentials(tenantId, gatewayId) }),
    });
}

export function useRevokeCredential(tenantId: string, gatewayId: string) {
    const { gateways } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (credentialId: string) => gateways.revokeCredential(tenantId, gatewayId, credentialId),
        onSuccess: () => qc.invalidateQueries({ queryKey: gatewayKeys.credentials(tenantId, gatewayId) }),
    });
}
